// filepath: /Users/benedikt/Documents/code/time-tracking/time-snap/src/pages/Clients.js
import React, { useState, useEffect } from 'react';
import ClientList from '../components/client/ClientList';
import ClientDialog from '../components/client/ClientDialog';
import Button from '../components/common/Button';
import LoadingSpinner from '../components/common/LoadingSpinner';
import ErrorAlert from '../components/common/ErrorAlert';
import { useApi } from '../hooks/useApi';
import { useAuth } from '../hooks/useAuth';

/**
 * Hauptkomponente für die Kundenverwaltung
 */
function Clients() {
  // State für den Dialog
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [currentClientId, setCurrentClientId] = useState(null);
  
  // State für Kundendaten
  const [clients, setClients] = useState([]);
  const [clientData, setClientData] = useState({
    name: '',
    taxNumber: '',
    vatRegistrationId: '',
    allowTaxFreeInvoices: true,
    contactPersons: [{
      salutation: '',
      firstName: '',
      lastName: '',
      primary: true,
      emailAddress: '',
      phoneNumber: ''
    }],
    addresses: {
      billing: [{
        supplement: '',
        street: '',
        zip: '',
        city: '',
        countryCode: 'DE'
      }],
      shipping: [{
        supplement: '',
        street: '',
        zip: '',
        city: '',
        countryCode: 'DE'
      }]
    },
    emailAddresses: {
      business: [''],
      office: [''],
      private: [''],
      other: ['']
    },
    phoneNumbers: {
      business: [''],
      office: [''],
      mobile: [''],
      private: [''],
      fax: [''],
      other: ['']
    },
    note: ''
  });

  // API und Auth Hooks
  const { loading, error, fetchClients, addClient, updateClient } = useApi();
  const { isAuthenticated } = useAuth();

  // Laden der Kunden beim ersten Rendern
  useEffect(() => {
    const loadClients = async () => {
      try {
        const clientsData = await fetchClients();
        setClients(clientsData);
      } catch (err) {
        console.error('Fehler beim Laden der Kunden:', err);
      }
    };
    
    loadClients();
  }, [fetchClients]);

  // Dialog zum Hinzufügen eines Kunden öffnen
  const handleAddClientClick = () => {
    setClientData({
      name: '',
      taxNumber: '',
      vatRegistrationId: '',
      allowTaxFreeInvoices: true,
      contactPersons: [{
        salutation: '',
        firstName: '',
        lastName: '',
        primary: true,
        emailAddress: '',
        phoneNumber: ''
      }],
      addresses: {
        billing: [{
          supplement: '',
          street: '',
          zip: '',
          city: '',
          countryCode: 'DE'
        }],
        shipping: [{
          supplement: '',
          street: '',
          zip: '',
          city: '',
          countryCode: 'DE'
        }]
      },
      emailAddresses: {
        business: [''],
        office: [''],
        private: [''],
        other: ['']
      },
      phoneNumbers: {
        business: [''],
        office: [''],
        mobile: [''],
        private: [''],
        fax: [''],
        other: ['']
      },
      note: ''
    });
    setIsEditMode(false);
    setCurrentClientId(null);
    setIsDialogOpen(true);
  };

  // Dialog zum Bearbeiten eines Kunden öffnen
  const handleEditClientClick = (client) => {
    setClientData({
      name: client.company.name,
      taxNumber: client.company.taxNumber,
      vatRegistrationId: client.company.vatRegistrationId,
      allowTaxFreeInvoices: client.company.allowTaxFreeInvoices,
      contactPersons: client.company.contactPersons,
      addresses: client.addresses,
      emailAddresses: client.emailAddresses,
      phoneNumbers: client.phoneNumbers,
      note: client.note
    });
    setIsEditMode(true);
    setCurrentClientId(client._id);
    setIsDialogOpen(true);
  };

  // Dialog schließen
  const handleCloseDialog = () => {
    setIsDialogOpen(false);
  };

  // Kunde speichern (hinzufügen oder bearbeiten)
  const handleSaveClient = async () => {
    try {
      if (isEditMode) {
        await updateClient(currentClientId, clientData);
      } else {
        await addClient(clientData);
      }
      
      // Dialog schließen und Kunden neu laden
      setIsDialogOpen(false);
      const updatedClients = await fetchClients();
      setClients(updatedClients);
    } catch (err) {
      console.error('Fehler beim Speichern des Kunden:', err);
    }
  };

  return (
    <div className="px-[90px] py-[112px]">
      <div className="flex justify-between items-center mb-[72px]">
        <h1 className="text-[60px] leading-[63px] font-bold">Aktuelle Kunden</h1>
        <Button
          variant="primary"
          onClick={handleAddClientClick}
        >
          Hinzufügen
        </Button>
      </div>
      
      {loading && <LoadingSpinner />}
      {error && <ErrorAlert message={error} />}
      
      {!loading && clients.length > 0 && (
        <ClientList 
          clients={clients} 
          onEditClient={handleEditClientClick} 
        />
      )}
      
      {!loading && clients.length === 0 && (
        <div className="text-center py-10">
          <p className="text-gray-500">Keine Kunden gefunden</p>
        </div>
      )}
      
      <ClientDialog
        isOpen={isDialogOpen}
        onClose={handleCloseDialog}
        onSave={handleSaveClient}
        clientData={clientData}
        setClientData={setClientData}
        error={error}
        isEdit={isEditMode}
      />
    </div>
  );
}

export default Clients;
